import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MEDIA_TYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
} from "../constants/constants";
import { useMemo, useState } from "react";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import Banner from "../../../DesignSystem/Banner/Banner";
import FileUpload from "../uploads/FileUpload";
import "../../../../css/Marketing/FinancialPromotions/FinancialPromotionsSubmission.css";
import "../../../../css/Marketing/FinancialPromotions/FinancialPromotions.css";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import { useAdviserListWithUnappointed } from "../../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { approveGeneratedFinancialPromotionResponse } from "../../FinancialPromotionContentGenerator/services/updateFinancialPromotionResponseStatus";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";

const normaliseText = (value) => {
  return typeof value === "string" ? value.trim() : "";
};

const getStoredPrefill = () => {
  try {
    const stored = sessionStorage.getItem(
      "financialPromotionSubmissionPrefill",
    );

    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const getMatchingOptionValue = (options = [], value = "") => {
  const cleanValue = normaliseText(value).toLowerCase();

  if (!cleanValue) return "";

  const matchedOption = options.find((option) => {
    const optionValue =
      typeof option === "string"
        ? option
        : option?.value || option?.label || "";

    const optionLabel =
      typeof option === "string"
        ? option
        : option?.label || option?.value || "";

    return (
      optionValue.toLowerCase() === cleanValue ||
      optionLabel.toLowerCase() === cleanValue
    );
  });

  if (!matchedOption) return "";

  return typeof matchedOption === "string"
    ? matchedOption
    : matchedOption.value;
};

const getDefaultMediaType = (prefill = {}) => {
  const contentTypeMatch = getMatchingOptionValue(
    MEDIA_TYPE_OPTIONS,
    prefill.contentType,
  );

  if (contentTypeMatch) return [contentTypeMatch];

  const platformMatch = getMatchingOptionValue(
    MEDIA_TYPE_OPTIONS,
    prefill.platform,
  );

  if (platformMatch) return [platformMatch];

  const socialMediaOption = MEDIA_TYPE_OPTIONS.find((option) =>
    String(option).toLowerCase().includes("social"),
  );

  if (socialMediaOption) return [socialMediaOption];

  return MEDIA_TYPE_OPTIONS.length ? [MEDIA_TYPE_OPTIONS[0]] : [];
};

const getDefaultProductTypes = (prefill = {}) => {
  const matchedProductType = getMatchingOptionValue(
    PRODUCT_TYPE_OPTIONS,
    prefill.contentType,
  );

  return matchedProductType ? [matchedProductType] : [];
};

const NewFinancialPromotions = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const prefill = useMemo(() => {
    return location.state?.prefill || getStoredPrefill();
  }, [location.state]);

  const [mediaTypes, setMediaTypes] = useState(() =>
    getDefaultMediaType(prefill),
  );

  const [productTypes, setProductTypes] = useState(() =>
    getDefaultProductTypes(prefill),
  );

  const [files, setFiles] = useState([]);
  const [text, setText] = useState(() => normaliseText(prefill.text));
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState(() => normaliseText(prefill.title));
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedAdviser, setSelectedAdviser] = useState(() =>
    normaliseText(prefill.selectedAdviser),
  );
  const [adviserError, setAdviserError] = useState("");

  const { advisers, loadingAdvisers } = useAdviserListWithUnappointed();

  const adviserOptions = [
    {
      id: "",
      name: loadingAdvisers ? "Loading advisers..." : "Select Adviser",
    },
    ...advisers.map((adv) => ({
      id: adv.email,
      name: `${adv.email} ${adv.name ? `(${adv.name})` : ""}`,
    })),
  ];

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      if (!selectedAdviser) {
        setAdviserError("Please select an adviser.");
        setLoading(false);
        return;
      }

      if (!text.trim() && !url.trim() && files.length === 0) {
        setError("Please provide content, a URL, or at least one file.");
        setLoading(false);
        return;
      }

      const formData = new FormData();

      const selectedAdviserObj = advisers.find(
        (a) => a.email === selectedAdviser,
      );

      const payload = {
        userEmail: selectedAdviser,
        createdByEmail: localStorage.getItem("userEmail") || "",
        title,
        text,
        url,
        notes: message,
        mediaTypes,
        productTypes,
        sourceResponseId: prefill.sourceResponseId || "",
        sourcePromotionId: prefill.sourcePromotionId || "",
        adviserMeta: {
          email: selectedAdviserObj?.email || selectedAdviser,
          name: selectedAdviserObj?.name || "",
        },
        userDataMeta: {
          email: selectedAdviserObj?.email || selectedAdviser,
          name: selectedAdviserObj?.name || "",
        },
      };

      formData.append("payload", JSON.stringify(payload));

      files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(
        `${process.env.REACT_APP_FINANCIAL_PROMOTIONS_API_URL}/fp/staff-create-approved`,
        {
          method: "POST",
          body: formData,
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Failed to create submission");
      }

      const hasGeneratedResponsePrefill =
        Boolean(prefill?.sourcePromotionId) ||
        Boolean(prefill?.sourceResponseId);

      if (hasGeneratedResponsePrefill) {
      await approveGeneratedFinancialPromotionResponse({
        sourceResponseId: prefill.sourceResponseId || "",
        approvedSubmissionId: data?.submissionId || data?.id || "",
        approvedByEmail: localStorage.getItem("userEmail") || "",
      });
      }

      sessionStorage.removeItem("financialPromotionSubmissionPrefill");

     if (hasGeneratedResponsePrefill) {
      navigate(APP_ROUTES.SOCIAL_MEDIA_CONTENT_MANAGEMENT, {
        state: {
          approvedResponseId: prefill.sourceResponseId || "",
        },
      });
     } else {
       navigate(APP_ROUTES.FINANCIAL_PROMOTIONS);
     }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Financial Promotions</title>
        <meta
          name="description"
          content="Admin page for uploading financial promotions for compliance review and approval."
        />
      </Helmet>

      <Sidebar />

      <div className="fp-submission-container">
        <Breadcrumb
          items={[
            {
              label: "Compliance",
              to: "/Department/Compliance",
            },
            {
              label: "Financial Promotions",
              to: "/FinancialPromotions",
            },
            { label: "New Submission" },
          ]}
        />
        <div className="fp-header-new">
          <div className="FP_H3">New Submission</div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();

            if (!selectedAdviser) {
              setAdviserError("Please select an adviser.");
              return;
            }

            setAdviserError("");
            handleSubmit();
          }}
        >
          <div className="fp-form-container">
            <div className="fp-form-column">
              <label className="FP_Body">
                Adviser <span className="fp-form-required">*</span>
              </label>

              <CustomSelect
                options={adviserOptions}
                value={selectedAdviser}
                onChange={(e) => {
                  setSelectedAdviser(e.target.value);
                  if (e.target.value) setAdviserError("");
                }}
                disabled={loading || loadingAdvisers}
                error={!!adviserError}
                placeholder={
                  loadingAdvisers ? "Loading advisers..." : "Select Adviser"
                }
                style={{ marginBottom: adviserError ? 0 : 16 }}
              />

              {adviserError && (
                <div
                  style={{ color: "#d32f2f", fontSize: 13, marginTop: 4 }}
                  className="FP_Help_Text_Semibold"
                >
                  {adviserError}
                </div>
              )}
            </div>

            <div className="fp-form-column">
              <label className="FP_Body">
                Content Type <span className="fp-form-required">*</span>
              </label>

              <div className="FP_Content">
                Please select all appropriate options for the content you want
                to submit for approval:
              </div>

              <div className="fp-form-checkbox-group">
                {MEDIA_TYPE_OPTIONS.map((type) => (
                  <label
                    key={type}
                    className="fp-form-checkbox-label ds-checkbox-label FP_Content"
                  >
                    <input
                      type="checkbox"
                      checked={mediaTypes.includes(type)}
                      onChange={() =>
                        setMediaTypes(
                          mediaTypes.includes(type)
                            ? mediaTypes.filter((t) => t !== type)
                            : [...mediaTypes, type],
                        )
                      }
                      disabled={loading}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>

            <div className="fp-form-column">
              <label className="FP_Body">What is this content about?</label>

              <div className="FP_Content">
                Please select any appropriate keywords related to the content:
              </div>

              <div className="fp-form-checkbox-group">
                {PRODUCT_TYPE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="fp-form-checkbox-label ds-checkbox-label FP_Content"
                  >
                    <input
                      type="checkbox"
                      checked={productTypes.includes(opt.value)}
                      onChange={() =>
                        setProductTypes(
                          productTypes.includes(opt.value)
                            ? productTypes.filter((v) => v !== opt.value)
                            : [...productTypes, opt.value],
                        )
                      }
                      disabled={loading}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="fp-form-column">
              <label className="fp-form-label FP_Body">Content Title</label>

              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title of Promotion / Subject"
                className="FP_Content fp-form-input"
                disabled={loading}
              />
            </div>

            <div className="fp-form-column">
              <label className="fp-form-label FP_Content">
                Content <span className="fp-form-required">*</span>
              </label>

              <div className="FP_Content">
                Enter all content for review. Max 5 files per submission. Please
                paste any URL link in the section below.
              </div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter your content here..."
                className="fp-form-input fp-form-textarea FP_Content"
                disabled={loading}
              />

              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="URL Link"
                className="fp-form-input FP_Content"
                disabled={loading}
              />

              <FileUpload
                label={null}
                description="Drag & drop files here or"
                browseText="Browse"
                status="default"
                disabled={loading}
                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp,.mp4,.mov,.avi,.mp3,.wav,.m4a"
                multiple
                files={files}
                onFilesChange={setFiles}
              />
            </div>

            {error && <Banner type="error" message={error} />}

            <div className="fp-form-actions">
              <PrimaryButton
                type="submit"
                loading={loading}
                label="Create & Approve"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewFinancialPromotions;
